import { useMemo } from "react";
import type { CSSObjectWithLabel, ControlProps, GroupBase, StylesConfig } from "react-select";
import Select, {
    components,
    type FilterOptionOption,
    type IndicatorsContainerProps,
    type OptionProps,
    type SingleValueProps,
} from "react-select";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import "./RegLookupSelect.css";

export type RegLookupSelectOption = {
    value: string;
    label: string;
    code: string;
    description: string;
};

export type RegLookupSelectItem = {
    id: string;
    code?: string | null;
    description: string;
};

function normalizeLookupSearch(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, "");
}

export function toRegLookupSelectOptions(
    items: ReadonlyArray<RegLookupSelectItem>,
    formatCode?: (code: string) => string
): RegLookupSelectOption[] {
    return items.map((item) => ({
        value: item.id,
        label: formatCode ? formatCode(item.code ?? "") : (item.code ?? ""),
        code: item.code ?? "",
        description: item.description,
    }));
}

/** Coded rows first (A–Z by code), then null/blank-code rows (A–Z by description). */
export function sortRegLookupItemsByCodeThenDescription(
    items: ReadonlyArray<RegLookupSelectItem>
): RegLookupSelectItem[] {
    return [...items].sort((a, b) => {
        const aCode = (a.code ?? "").trim();
        const bCode = (b.code ?? "").trim();
        const aHasCode = aCode.length > 0;
        const bHasCode = bCode.length > 0;
        if (aHasCode !== bHasCode) return aHasCode ? -1 : 1;
        if (aHasCode && bHasCode) {
            const byCode = aCode.localeCompare(bCode, undefined, { sensitivity: "base" });
            if (byCode !== 0) return byCode;
        }
        return a.description.localeCompare(b.description, undefined, { sensitivity: "base" });
    });
}

export function filterRegLookupOption(
    option: FilterOptionOption<RegLookupSelectOption>,
    inputValue: string,
    codeOnly = false,
    descriptionOnly = false
): boolean {
    const q = normalizeLookupSearch(inputValue);
    if (!q) return true;
    const { code, description } = option.data;
    if (descriptionOnly) return description.toLowerCase().includes(q);
    const codeNorm = normalizeLookupSearch(code ?? "");
    if (codeOnly) return codeNorm.includes(q);
    return codeNorm.includes(q) || description.toLowerCase().includes(q);
}

export function formatRegLookupDisplayLabel(code?: string | null, description?: string | null): string {
    const trimmedCode = (code ?? "").trim();
    const trimmedDescription = (description ?? "").trim();
    if (!trimmedCode && !trimmedDescription) return "";
    if (!trimmedCode) return trimmedDescription;
    if (!trimmedDescription) return trimmedCode;
    return `${trimmedCode} — ${trimmedDescription}`;
}

export function formatRegLookupOptionLabel(
    option: RegLookupSelectOption,
    codeOnly = false,
    descriptionOnly = false,
    formatCode?: (code: string) => string
): string {
    if (descriptionOnly) return option.description;
    const code = formatCode ? formatCode(option.code ?? "") : (option.code ?? "");
    if (codeOnly) return code;
    if (!code.trim()) return option.description;
    return option.description ? `${code} — ${option.description}` : code;
}

function RegLookupSelectLabel({
    data,
    codeOnly,
    descriptionOnly,
    formatCode,
}: {
    data: RegLookupSelectOption;
    codeOnly: boolean;
    descriptionOnly: boolean;
    formatCode?: (code: string) => string;
}) {
    if (descriptionOnly || !(data.code ?? "").trim()) {
        return <>{data.description}</>;
    }

    const code = formatCode ? formatCode(data.code ?? "") : (data.code ?? "");

    if (codeOnly) {
        return <>{code}</>;
    }

    return (
        <>
            {code}
            <span className="text-muted"> — </span>
            <span className="text-muted fst-italic">{data.description}</span>
        </>
    );
}

function RegLookupSelectMenuOption(
    props: OptionProps<RegLookupSelectOption, false>,
    codeOnly: boolean,
    descriptionOnly: boolean,
    formatCode?: (code: string) => string
) {
    return (
        <components.Option {...props}>
            <RegLookupSelectLabel data={props.data} codeOnly={codeOnly} descriptionOnly={descriptionOnly} formatCode={formatCode} />
        </components.Option>
    );
}

function RegLookupSelectSingleValue(
    props: SingleValueProps<RegLookupSelectOption, false>,
    codeOnly: boolean,
    descriptionOnly: boolean,
    formatCode?: (code: string) => string
) {
    return (
        <components.SingleValue {...props}>
            <RegLookupSelectLabel data={props.data} codeOnly={codeOnly} descriptionOnly={descriptionOnly} formatCode={formatCode} />
        </components.SingleValue>
    );
}

function RegLookupSelectIndicators(
    props: IndicatorsContainerProps<RegLookupSelectOption, false>,
    invalid: boolean
) {
    return (
        <components.IndicatorsContainer {...props}>
            {invalid ? <span className="reg-lookup-select__feedback-icon" aria-hidden="true" /> : null}
            {props.children}
        </components.IndicatorsContainer>
    );
}

type RegLookupSelectProps = {
    inputId?: string;
    value: string;
    onChange: (value: string) => void;
    items: ReadonlyArray<RegLookupSelectItem>;
    placeholder: string;
    /** When true, filter and display use code only (no description). */
    codeOnly?: boolean;
    /** When true, filter and display use description only (no code). */
    descriptionOnly?: boolean;
    /** Optional formatter for code display (e.g. EWC `20 01 01`). Filtering still uses the raw code. */
    formatCode?: (code: string) => string;
    invalid?: boolean;
    /** Render the menu in a body portal to avoid clipping inside overflow containers. */
    portMenu?: boolean;
    /** Fill the parent width and truncate long selected labels. */
    fillContainer?: boolean;
    isClearable?: boolean;
    disabled?: boolean;
};

export function RegLookupSelect({
    inputId,
    value,
    onChange,
    items,
    placeholder,
    codeOnly = false,
    descriptionOnly = false,
    formatCode,
    invalid = false,
    portMenu = false,
    fillContainer = false,
    isClearable = false,
    disabled = false,
}: RegLookupSelectProps) {
    const options = useMemo(() => toRegLookupSelectOptions(items, formatCode), [items, formatCode]);
    const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);
    const styles = useMemo((): StylesConfig<RegLookupSelectOption, false, GroupBase<RegLookupSelectOption>> => {
        type LookupControlProps = ControlProps<RegLookupSelectOption, false, GroupBase<RegLookupSelectOption>>;

        const menuPortalStyle = portMenu
            ? {
                  menuPortal: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
                      ...provided,
                      zIndex: 1060,
                  }),
              }
            : {};

        const fillContainerStyle = fillContainer
            ? {
                  container: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
                      ...provided,
                      width: "100%",
                      minWidth: 0,
                  }),
                  valueContainer: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
                      ...provided,
                      flexWrap: "nowrap",
                      overflow: "hidden",
                  }),
                  singleValue: (provided: CSSObjectWithLabel): CSSObjectWithLabel => ({
                      ...provided,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                  }),
              }
            : {};

        const controlStyle = (provided: CSSObjectWithLabel, state: LookupControlProps): CSSObjectWithLabel => {
            const base = ReactSelectBootstrapStyle.control?.(provided, state) ?? provided;
            const withFill = fillContainer ? { ...base, minWidth: 0 } : base;
            if (!invalid) return withFill;

            return {
                ...withFill,
                borderColor: "#dc3545",
                ...(state.isFocused
                    ? { boxShadow: "0 0 0 0.25rem rgba(220, 53, 69, 0.25)" }
                    : {}),
            };
        };

        return {
            ...ReactSelectBootstrapStyle,
            ...menuPortalStyle,
            ...fillContainerStyle,
            control: controlStyle,
        };
    }, [invalid, portMenu, fillContainer]);

    return (
        <Select<RegLookupSelectOption, false>
            inputId={inputId}
            options={options}
            value={selected}
            onChange={(option) => onChange(option?.value ?? "")}
            placeholder={placeholder}
            isClearable={isClearable}
            isDisabled={disabled}
            menuPortalTarget={portMenu ? document.body : undefined}
            menuPosition={portMenu ? "fixed" : "absolute"}
            filterOption={(option, inputValue) => filterRegLookupOption(option, inputValue, codeOnly, descriptionOnly)}
            components={{
                Option: (props) => RegLookupSelectMenuOption(props, codeOnly, descriptionOnly, formatCode),
                SingleValue: (props) => RegLookupSelectSingleValue(props, codeOnly, descriptionOnly, formatCode),
                IndicatorsContainer: (props) => RegLookupSelectIndicators(props, invalid),
            }}
            styles={styles}
            className={fillContainer ? "reg-lookup-select reg-lookup-select--fill" : undefined}
            classNamePrefix="react-select"
            aria-invalid={invalid}
        />
    );
}
